export const PRICING_SCHEMAS = {
  durations: {
    title: 'Booking Durations',
    description: 'Define duration/time-slot options customers can pick. Fully customizable — add any duration like 8 hours, 30 hours or 2 Nights 3 Days.',
    itemLabel: 'Duration',
    nameField: 'label',
    fields: [
      { key: 'label', label: 'Label', type: 'text', placeholder: 'e.g. 24 Hours / 2 Nights 3 Days' },
      { key: 'hours', label: 'Duration (hours)', type: 'number' },
      { key: 'check_in_time', label: 'Check-in time', type: 'time' },
      { key: 'check_out_time', label: 'Check-out time', type: 'time' }
    ]
  },
  venue_packages: {
    title: 'Venue Packages (Model A)',
    description: 'Entire venue sold as one package for a duration.',
    itemLabel: 'Package',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Package name', type: 'text', placeholder: 'Entire Venue · 24 Hours' },
      { key: 'duration_hours', label: 'Duration (hours)', type: 'number' },
      { key: 'check_in_time', label: 'Check-in time', type: 'time' },
      { key: 'check_out_time', label: 'Check-out time', type: 'time' },
      { key: 'base_price', label: 'Base price (₹)', type: 'number' },
      { key: 'max_guests', label: 'Max guest capacity', type: 'number' },
      { key: 'included_guests', label: 'Guests included in base price (min pax)', type: 'number' },
      { key: 'extra_guest_charge', label: 'Additional cost per head beyond included (₹)', type: 'number' },
      { key: 'max_overnight_guests', label: 'Max overnight stay capacity', type: 'number' },
      { key: 'included_room_ids', label: 'Included rooms', type: 'multiselect', optionsKey: 'rooms', allowAll: true },
      { key: 'included_space_ids', label: 'Included event spaces', type: 'multiselect', optionsKey: 'spaces', allowAll: true },
      { key: 'included_facilities', label: 'Included facilities', type: 'tags', placeholder: 'Parking, Power backup, Pool access' },
      { key: 'includes_food', label: 'Food included in this package', type: 'bool' },
      { key: 'extra_hour_charge', label: 'Additional hour charge (₹/hour)', type: 'number' }
    ]
  },
  per_person_packages: {
    title: 'Per-Person Packages (Model D)',
    description: 'Venue charged per guest. Supports slabs, min/max guests, stay/food/venue inclusive flags.',
    itemLabel: 'Package',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Package name', type: 'text' },
      { key: 'price_per_person', label: 'Price per person (₹)', type: 'number' },
      { key: 'child_price', label: 'Child price (₹)', type: 'number' },
      { key: 'min_guests', label: 'Minimum guaranteed guests', type: 'number' },
      { key: 'max_guests', label: 'Maximum guests', type: 'number' },
      { key: 'includes_stay', label: 'Stay inclusive', type: 'bool' },
      { key: 'includes_food', label: 'Food inclusive', type: 'bool' },
      { key: 'includes_venue', label: 'Venue/event-spaces inclusive', type: 'bool' },
      {
        key: 'slabs', label: 'Guest-count pricing slabs', type: 'objlist',
        columns: [
          { key: 'min_guests', label: 'Min guests', type: 'number' },
          { key: 'max_guests', label: 'Max guests', type: 'number' },
          { key: 'price', label: 'Price/person (₹)', type: 'number' }
        ]
      }
    ]
  },
  event_spaces: {
    title: 'Event Spaces',
    description: 'Create the venue\'s event spaces with capacities, slots and pricing.',
    itemLabel: 'Event Space',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Space name', type: 'text', placeholder: 'Pool Lawn' },
      { key: 'space_type', label: 'Space type', type: 'text', placeholder: 'Lawn / Hall / Amphitheatre' },
      { key: 'setting', label: 'Setting', type: 'select', options: [['indoor', 'Indoor'], ['outdoor', 'Outdoor'], ['semi_indoor', 'Semi-indoor']] },
      { key: 'floating_capacity', label: 'Max floating capacity', type: 'number' },
      { key: 'seated_capacity', label: 'Max seated capacity', type: 'number' },
      { key: 'dining_capacity', label: 'Dining capacity', type: 'number' },
      { key: 'suitable_events', label: 'Suitable event types', type: 'tags', placeholder: 'Mehendi, Sangeet, Wedding' },
      {
        key: 'time_slots', label: 'Time slots (each with its own price)', type: 'objlist',
        columns: [
          { key: 'label', label: 'Slot label', type: 'text' },
          { key: 'start', label: 'Start', type: 'time' },
          { key: 'end', label: 'End', type: 'time' },
          { key: 'price', label: 'Price (₹)', type: 'number' }
        ]
      },
      { key: 'price_per_hour', label: 'Cost per hour (₹)', type: 'number' },
      { key: 'full_day_price', label: 'Full-day cost (₹)', type: 'number' },
      { key: 'setup_time_hours', label: 'Setup time allowed (hours)', type: 'number' },
      { key: 'dismantle_time_hours', label: 'Dismantling time allowed (hours)', type: 'number' },
      { key: 'noise_restrictions', label: 'Noise restrictions', type: 'textarea' },
      { key: 'decor_restrictions', label: 'Décor restrictions', type: 'textarea' },
      { key: 'inclusions', label: 'Included (furniture, lighting, power…)', type: 'tags' },
      { key: 'extra_usage_charge', label: 'Additional usage charges (₹)', type: 'number' }
    ]
  },
  room_categories: {
    title: 'Room Inventory (Model C)',
    description: 'Configure each room category with occupancy, pricing and policies.',
    itemLabel: 'Room Category',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Room category name', type: 'text', placeholder: 'Deluxe Room' },
      { key: 'total_rooms', label: 'Number of rooms available', type: 'number' },
      { key: 'standard_occupancy', label: 'Standard occupancy', type: 'number' },
      { key: 'max_occupancy', label: 'Maximum occupancy', type: 'number' },
      { key: 'beds', label: 'Number of beds', type: 'number' },
      { key: 'bed_type', label: 'Bed type', type: 'text', placeholder: 'King / Twin' },
      { key: 'extra_guest_allowed', label: 'Extra guest allowed', type: 'bool' },
      { key: 'max_extra_guests', label: 'Max extra guests per room', type: 'number' },
      { key: 'extra_guest_charge', label: 'Extra guest charge (₹/night)', type: 'number' },
      { key: 'extra_mattress_charge', label: 'Extra mattress charge (₹/night)', type: 'number' },
      { key: 'child_policy', label: 'Child policy', type: 'textarea' },
      { key: 'child_price', label: 'Child price (₹/night)', type: 'number' },
      { key: 'price_per_night', label: 'Room price per night (₹)', type: 'number' },
      { key: 'weekend_price_per_night', label: 'Weekend price per night (₹)', type: 'number' },
      { key: 'buyout_price_per_night', label: 'Full inventory buyout price/night (₹)', type: 'number' },
      { key: 'min_rooms', label: 'Minimum room booking', type: 'number' },
      { key: 'complimentary_rooms', label: 'Complimentary rooms', type: 'number' },
      { key: 'check_in_time', label: 'Check-in time', type: 'time' },
      { key: 'check_out_time', label: 'Check-out time', type: 'time' },
      { key: 'early_checkin_charge', label: 'Early check-in charge (₹)', type: 'number' },
      { key: 'late_checkout_charge', label: 'Late check-out charge (₹)', type: 'number' },
      { key: 'meal_inclusions', label: 'Meal inclusions', type: 'tags', placeholder: 'Breakfast, Dinner' }
    ]
  },
  food_packages: {
    title: 'Food Packages',
    description: 'Create packages like Silver, Gold, Platinum with per-person / per-meal / fixed pricing and guest slabs.',
    itemLabel: 'Food Package',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Package name', type: 'text', placeholder: 'Gold Package' },
      { key: 'pricing_mode', label: 'Pricing mode', type: 'select', options: [['per_person', 'Per person'], ['per_meal', 'Per meal'], ['per_day', 'Per day'], ['per_event', 'Per event'], ['fixed', 'Fixed package']] },
      { key: 'price_veg', label: 'Vegetarian price (₹)', type: 'number' },
      { key: 'price_nonveg', label: 'Non-vegetarian price (₹)', type: 'number' },
      { key: 'child_price', label: 'Child price (₹)', type: 'number' },
      { key: 'fixed_price', label: 'Fixed package price (₹, if fixed mode)', type: 'number' },
      { key: 'min_guests', label: 'Minimum guaranteed guests', type: 'number' },
      { key: 'max_guests', label: 'Maximum guests', type: 'number' },
      { key: 'meals_included', label: 'Meal types included', type: 'tags', placeholder: 'Breakfast, Lunch, High Tea, Dinner, Midnight snacks' },
      { key: 'menu_items_count', label: 'Number of menu items', type: 'number' },
      { key: 'cuisines', label: 'Cuisine options', type: 'tags' },
      { key: 'live_counters', label: 'Live counters', type: 'number' },
      { key: 'inclusions', label: 'Other inclusions (welcome drinks…)', type: 'tags' },
      {
        key: 'slabs', label: 'Guest slabs (price overrides)', type: 'objlist',
        columns: [
          { key: 'min_guests', label: 'Min', type: 'number' },
          { key: 'max_guests', label: 'Max', type: 'number' },
          { key: 'price_veg', label: 'Veg ₹', type: 'number' },
          { key: 'price_nonveg', label: 'Non-veg ₹', type: 'number' }
        ]
      },
      { key: 'min_billing', label: 'Minimum billing amount (₹)', type: 'number' },
      { key: 'included_in_venue', label: 'Included in venue cost', type: 'bool' },
      { key: 'mandatory', label: 'Mandatory with venue booking', type: 'bool' }
    ]
  },
  addons: {
    title: 'Add-Ons & Custom Components (Model E)',
    description: 'Unlimited custom chargeable items — décor, DJ, generator, early check-in, anything.',
    itemLabel: 'Add-On',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Name', type: 'text', placeholder: 'DJ / Generator / Early check-in' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'pricing_type', label: 'Pricing type', type: 'select', options: [['fixed', 'Fixed price'], ['per_person', 'Per person'], ['per_hour', 'Per hour'], ['per_day', 'Per day'], ['per_quantity', 'Quantity-based']] },
      { key: 'price', label: 'Price (₹)', type: 'number' },
      { key: 'unit_label', label: 'Unit label (for quantity-based)', type: 'text', placeholder: 'chair/day' },
      { key: 'tax_percent', label: 'Tax %', type: 'number' },
      { key: 'mandatory', label: 'Mandatory (auto-added to every estimate)', type: 'bool' },
      { key: 'applicable_spaces', label: 'Applicable event spaces', type: 'multiselect', optionsKey: 'spaces' }
    ]
  },
  taxes_charges: {
    title: 'Taxes, Charges & Deposits',
    description: 'GST, service charges, security deposits, cleaning charges etc.',
    itemLabel: 'Tax / Charge',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Name', type: 'text', placeholder: 'GST (18%)' },
      { key: 'kind', label: 'Kind', type: 'select', options: [['tax', 'Tax'], ['charge', 'Charge'], ['deposit', 'Deposit']] },
      { key: 'type', label: 'Type', type: 'select', options: [['percent', 'Percentage'], ['fixed', 'Fixed amount']] },
      { key: 'value', label: 'Value (% or ₹)', type: 'number' },
      { key: 'applies_to', label: 'Applies to', type: 'select', options: [['all', 'Entire estimate'], ['venue', 'Venue'], ['rooms', 'Rooms'], ['food', 'Food'], ['event_spaces', 'Event spaces'], ['addons', 'Add-ons']] },
      { key: 'refundable', label: 'Refundable', type: 'bool' }
    ]
  },
  discounts: {
    title: 'Discounts & Offers',
    description: 'Fixed/percentage discounts, promo codes, early-bird, bulk-room and guest-count offers.',
    itemLabel: 'Discount',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Name', type: 'text', placeholder: 'Early Bird Offer' },
      { key: 'type', label: 'Type', type: 'select', options: [['percent', 'Percentage'], ['fixed', 'Fixed amount']] },
      { key: 'value', label: 'Value (% or ₹)', type: 'number' },
      { key: 'promo_code', label: 'Promo code (leave empty for automatic)', type: 'text' },
      { key: 'conditions.early_days', label: 'Min days before check-in (early booking)', type: 'number' },
      { key: 'conditions.min_nights', label: 'Min nights (long duration)', type: 'number' },
      { key: 'conditions.min_rooms', label: 'Min rooms (bulk booking)', type: 'number' },
      { key: 'conditions.min_guests', label: 'Min guests', type: 'number' },
      { key: 'conditions.min_amount', label: 'Min estimate amount (₹)', type: 'number' },
      { key: 'valid_from', label: 'Valid from', type: 'date' },
      { key: 'valid_to', label: 'Valid to', type: 'date' }
    ]
  },
  seasonal_pricing: {
    title: 'Seasonal & Dynamic Pricing',
    description: 'Weekend, peak season, wedding season or day-of-week multipliers. Date-range rules take priority over day-of-week rules.',
    itemLabel: 'Pricing Rule',
    nameField: 'name',
    fields: [
      { key: 'name', label: 'Rule name', type: 'text', placeholder: 'Weekend Pricing / Wedding Season' },
      { key: 'multiplier', label: 'Price multiplier (e.g. 1.2 = +20%)', type: 'number', step: '0.05' },
      { key: 'date_from', label: 'Date from (for season)', type: 'date' },
      { key: 'date_to', label: 'Date to (for season)', type: 'date' },
      { key: 'days_of_week', label: 'Days of week (for weekend rules)', type: 'tags', placeholder: 'Sat, Sun' }
    ]
  },
  special_dates: {
    title: 'Special Date Overrides',
    description: 'Override pricing for a specific date (highest priority).',
    itemLabel: 'Special Date',
    nameField: 'label',
    fields: [
      { key: 'label', label: 'Label', type: 'text', placeholder: 'New Year\'s Eve' },
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'multiplier', label: 'Price multiplier', type: 'number', step: '0.05' }
    ]
  },
  rules: {
    title: 'Rules & Regulations',
    description: 'Policies shown on the calculator and PDF. Customers must accept mandatory rules before enquiry/download.',
    itemLabel: 'Rule',
    nameField: 'title',
    fields: [
      { key: 'category', label: 'Category', type: 'text', placeholder: 'Alcohol Policy / Music & Noise / Cancellation' },
      { key: 'title', label: 'Rule title', type: 'text' },
      { key: 'body', label: 'Rule details', type: 'textarea' },
      { key: 'important', label: 'Mark as important', type: 'bool' },
      { key: 'mandatory_acceptance', label: 'Mandatory acceptance', type: 'bool' },
      { key: 'show_in_calculator', label: 'Show on calculator', type: 'bool' },
      { key: 'show_in_pdf', label: 'Show in PDF', type: 'bool' }
    ]
  }
};

export const FOOD_AVAILABILITY_OPTIONS = [
  ['yes', 'Yes — in-house food available'],
  ['no', 'No in-house food'],
  ['optional', 'Optional'],
  ['mandatory', 'Mandatory with booking'],
  ['external_allowed', 'External caterers allowed']
];
